import { useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import Dataset from "datasets/types/interfaces/dataset";
import { AlertCircle, PanelRightClose, PanelRightOpen } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useNextGenHeader from "~/contexts/nextgen-header";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import { nextgenConversationPath, nextgenProjectsListPath } from "~/pages/manifold-nextgen/paths";
import { Agent } from "~/services/agents";
import {
  Project,
  ProjectConversation,
  projectAgentsPath,
  projectConversationsPath,
  projectDatasetsPath,
  projectPath,
} from "~/services/projects";
import { NewConversationDialog } from "./NewConversationDialog";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { ProjectSettingsDialog } from "./ProjectSettingsDialog";
import { ProjectPerson, ProjectSidebar } from "./ProjectSidebar";

const { VITE_CATALOG_API } = import.meta.env;
const CATALOG_DATASETS_URL = `${VITE_CATALOG_API}/datasets?offset=0&limit=1000&sort_by=title&sort_order=asc`;

export interface ProjectOutletContext {
  projectId: string;
  project: Project;
  conversations: ProjectConversation[] | undefined;
  attachedAgents: Agent[] | undefined;
  onNewConversation: () => void;
}

export const ProjectLayout = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const {
    data: project,
    error,
    isLoading,
  } = useFetcher<Project>(projectId ? projectPath(projectId) : null);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [agentsOpen, setAgentsOpen] = useState(false);
  const [datasetsOpen, setDatasetsOpen] = useState(false);
  const [newConversationOpen, setNewConversationOpen] = useState(false);

  const { data: attachedAgents } = useFetcher<Agent[]>(
    projectId ? projectAgentsPath(projectId) : null
  );

  const { data: attachedDatasetIds } = useFetcher<string[]>(
    projectId ? projectDatasetsPath(projectId) : null
  );

  const { data: catalogDatasets } = useFetcher<Dataset[]>(CATALOG_DATASETS_URL);

  const { data: conversationsList } = useFetcher<{ items: ProjectConversation[] }>(
    projectId ? projectConversationsPath(projectId) : null
  );
  const conversations = conversationsList?.items;

  const attachedDatasets = useMemo<Dataset[] | undefined>(() => {
    if (attachedDatasetIds === undefined || catalogDatasets === undefined) return undefined;
    const byId = new Map(catalogDatasets.map((d) => [d.id, d]));
    return attachedDatasetIds.map((id) => byId.get(id)).filter((d): d is Dataset => Boolean(d));
  }, [attachedDatasetIds, catalogDatasets]);

  const currentUser = useUser();
  const people = useMemo<ProjectPerson[] | undefined>(() => {
    if (!project) return undefined;
    const ownerId = project.owner_user_id;
    const label =
      currentUser?.user_id === ownerId && currentUser?.email ? currentUser.email : ownerId;
    return [{ user_id: ownerId, label }];
  }, [project, currentUser]);

  const breadcrumbs = useMemo(
    () => [
      { label: "Projects", href: nextgenProjectsListPath() },
      { label: project?.name ?? "Loading…" },
    ],
    [project?.name]
  );

  const rightPane = useMemo(() => {
    const toggle = (
      <Button
        variant="ghost"
        size="icon"
        className="size-8 text-muted-foreground"
        onClick={() => setSidebarOpen((o) => !o)}
        aria-label={sidebarOpen ? "Collapse project sidebar" : "Expand project sidebar"}
      >
        {sidebarOpen ? (
          <PanelRightClose className="size-4.5" />
        ) : (
          <PanelRightOpen className="size-4.5" />
        )}
      </Button>
    );
    return sidebarOpen ? (
      <div className="flex w-72 items-center justify-end border-l border-border px-3">{toggle}</div>
    ) : (
      <div className="flex items-center justify-end px-6">{toggle}</div>
    );
  }, [sidebarOpen]);

  useNextGenHeader({
    breadcrumbs,
    rightPane,
  });

  if (!projectId) {
    return (
      <div role="alert" className="p-6 text-sm text-destructive">
        Missing project id.
      </div>
    );
  }

  const sidebar = sidebarOpen ? (
    <ProjectSidebar
      projectId={projectId}
      onSettings={() => setSettingsOpen(true)}
      onAddAgent={() => setAgentsOpen(true)}
      onAddDataset={() => setDatasetsOpen(true)}
      onNewConversation={() => setNewConversationOpen(true)}
      attachedAgents={attachedAgents}
      attachedDatasets={attachedDatasets}
      conversations={conversations}
      people={people}
    />
  ) : null;

  if (isLoading) {
    return (
      <div className="flex h-full overflow-hidden">
        <div
          className="flex flex-1 flex-col overflow-hidden"
          role="status"
          aria-label="Loading project"
        />
        {sidebar}
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex h-full overflow-hidden">
        <div
          role="alert"
          className="flex flex-1 flex-col items-center justify-center gap-3 text-center text-destructive"
        >
          <AlertCircle className="size-8" aria-hidden="true" />
          <p className="text-sm font-medium">Failed to load project</p>
        </div>
        {sidebar}
      </div>
    );
  }

  const outletContext: ProjectOutletContext = {
    projectId,
    project,
    conversations,
    attachedAgents,
    onNewConversation: () => setNewConversationOpen(true),
  };

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <div className="flex flex-1 flex-col overflow-hidden">
          <Outlet context={outletContext} />
        </div>
        {sidebar}
      </div>

      <ProjectSettingsDialog project={project} open={settingsOpen} onOpenChange={setSettingsOpen} />

      <ProjectAgentsDialog
        projectId={projectId}
        open={agentsOpen}
        onOpenChange={setAgentsOpen}
        attached={attachedAgents ?? []}
      />

      <ProjectDatasetsDialog
        projectId={projectId}
        open={datasetsOpen}
        onOpenChange={setDatasetsOpen}
        attachedIds={attachedDatasetIds ?? []}
      />

      <NewConversationDialog
        projectId={projectId}
        open={newConversationOpen}
        onOpenChange={setNewConversationOpen}
        attachedAgents={attachedAgents ?? []}
        attachedDatasets={attachedDatasets ?? []}
        onCreated={(conversationId) => {
          mutate(projectConversationsPath(projectId));
          navigate(nextgenConversationPath(projectId, conversationId));
        }}
      />
    </>
  );
};
